import { useAuth } from '../../context/AuthContext';
import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { buildTenantPath, extractTenantSlugFromPathname } from '../../utils/tenantSlug';

const ClientSelectionPage = () => {
  const { availableClients, selectClient, logout, user, isLoading } = useAuth();
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectedClientId, setSelectedClientId] = useState<string>('');
  const navigate = useNavigate();
  const location = useLocation();
  const tenantSlug = extractTenantSlugFromPathname(location.pathname);

  useEffect(() => {
    if (user?.userGlobalRole === "superAdmin") {
      navigate(buildTenantPath("/super-admin", tenantSlug), { replace: true });
      return;
    }

    if (availableClients.length === 1) {
      const client = availableClients[0];
      console.log(`Redirecting to single client: ${client.clientName}`);
      selectClient(client.clientId);
    }
  }, [availableClients, navigate, selectClient, user?.userGlobalRole]);

  const handleSelectClient = async () => {
    if (!selectedClientId) return;
    setIsSelecting(true);
    try {
      await selectClient(selectedClientId);
    } catch (error) {
      console.error('Failed to select client', error);
      setIsSelecting(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-neutral-50 py-20">
      <div className="container mx-auto px-4">
        <div className="max-w-md mx-auto">
            <button 
                onClick={logout}
                className="inline-flex items-center text-sm text-neutral-600 hover:text-primary-600 mb-8"
            >
                <ArrowLeft size={16} className="mr-1" />
                Logout / Trocar Usuário
            </button>

          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="text-center mb-8">
              <h1 className="text-2xl font-display font-bold text-neutral-900 mb-2">
                Selecione uma Clínica
              </h1>
              <p className="text-neutral-600">
                Olá, {user?.userDisplayName || 'usuário'}! Escolha qual clínica você deseja acessar.
              </p>
            </div>

            {availableClients.length > 0 ? (
              <div className="space-y-6">
                <div>
                    <label htmlFor="client-select" className="block text-sm font-medium text-neutral-700 mb-1">
                        Clínicas disponíveis
                    </label>
                    <select
                        id="client-select"
                        value={selectedClientId}
                        onChange={(e) => setSelectedClientId(e.target.value)}
                        className="block w-full pl-3 pr-10 py-2 border border-neutral-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500"
                    >
                        <option value="" disabled>-- Escolha uma clínica --</option>
                        {availableClients.map((client) => (
                            <option key={client.clientId} value={client.clientId}>
                                {client.clientName}
                            </option>
                        ))}
                    </select>
                </div>

                <button
                    onClick={handleSelectClient}
                    disabled={isSelecting || !selectedClientId}
                    className="w-full flex justify-center py-3 px-4 border border-transparent rounded-md shadow-sm text-lg font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-colors"
                >
                    {isSelecting ? 'Entrando...' : 'Acessar Clínica'}
                </button>
              </div>
            ) : (
              <div className="text-center">
                 <p className="text-neutral-700">
                    Você não está associado a nenhuma clínica no momento.
                 </p>
                 <p className="text-sm text-neutral-500 mt-2">
                    Por favor, entre em contato com o administrador para obter acesso.
                 </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClientSelectionPage;
